
export const dynamic = 'force-dynamic';

import { Link } from 'react-router-dom';
import DashboardLayout from "../components/DashboardLayout";
import {
  FileSpreadsheet,
  TrendingUp,
  Users,
  Receipt,
  CreditCard,
  Calendar,
  Package,
  Building2,
  ArrowRight,
  BarChart3,
  Wallet,
  Truck,
  HandCoins,
  Scale,
  Landmark,
  BedDouble,
} from "lucide-react";
import { useCanSeeMoney } from "./ReportKit";

interface ReportCard {
  title: string;
  description: string;
  href: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
  /** Only for roles that may see revenue, cost and profit */
  moneyOnly?: boolean;
  isNew?: boolean;
}

interface Section {
  title: string;
  reports: ReportCard[];
}

// Every link here is a route in src/App.tsx
const SECTIONS: Section[] = [
  {
    title: "Money",
    reports: [
      {
        title: "Money Owed",
        description: "Who still has to pay, how late it is, with call and WhatsApp reminders",
        href: "/reports/receivables",
        icon: HandCoins,
        color: "text-rose-600",
        bgColor: "bg-rose-50",
        borderColor: "border-rose-200 hover:border-rose-400",
        isNew: true,
      },
      {
        title: "Profit & Loss",
        description: "What each month earned after costs and overheads, vs last year",
        href: "/reports/profit-loss",
        icon: Scale,
        color: "text-emerald-700",
        bgColor: "bg-emerald-50",
        borderColor: "border-emerald-200 hover:border-emerald-400",
        moneyOnly: true,
        isNew: true,
      },
      {
        title: "GST Report",
        description: "Invoices with client GSTIN, CGST and SGST, for your accountant",
        href: "/reports/gst",
        icon: Landmark,
        color: "text-violet-600",
        bgColor: "bg-violet-50",
        borderColor: "border-violet-200 hover:border-violet-400",
        moneyOnly: true,
        isNew: true,
      },
      {
        title: "Financial Summary",
        description: "Money received and paid out, GST and profit",
        href: "/reports/financial-summary",
        icon: FileSpreadsheet,
        color: "text-emerald-600",
        bgColor: "bg-emerald-50",
        borderColor: "border-emerald-200 hover:border-emerald-400",
        moneyOnly: true,
      },
      {
        title: "Revenue Trends",
        description: "Received, paid out and profit month by month",
        href: "/reports/revenue",
        icon: TrendingUp,
        color: "text-orange-600",
        bgColor: "bg-orange-50",
        borderColor: "border-orange-200 hover:border-orange-400",
        moneyOnly: true,
      },
      {
        title: "Client Payments",
        description: "Every payment received from clients",
        href: "/client-payments",
        icon: CreditCard,
        color: "text-rose-600",
        bgColor: "bg-rose-50",
        borderColor: "border-rose-200 hover:border-rose-400",
      },
      {
        title: "Vendor Payments",
        description: "Every payment made to vendors",
        href: "/vendor-payments",
        icon: Wallet,
        color: "text-cyan-600",
        bgColor: "bg-cyan-50",
        borderColor: "border-cyan-200 hover:border-cyan-400",
      },
    ],
  },
  {
    title: "Events & clients",
    reports: [
      {
        title: "Event Reports",
        description: "Every event with totals for the filters you pick",
        href: "/reports/events",
        icon: Calendar,
        color: "text-sky-600",
        bgColor: "bg-sky-50",
        borderColor: "border-sky-200 hover:border-sky-400",
      },
      {
        title: "Client Analytics",
        description: "Per client: events, payments and what is still owed",
        href: "/reports/clients",
        icon: Users,
        color: "text-blue-600",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200 hover:border-blue-400",
      },
      {
        title: "Enquiry Funnel",
        description: "How many enquiries got a quote and booked",
        href: "/reports/enquiries",
        icon: BarChart3,
        color: "text-indigo-600",
        bgColor: "bg-indigo-50",
        borderColor: "border-indigo-200 hover:border-indigo-400",
      },
      {
        title: "Package Analytics",
        description: "Bookings and profit per package",
        href: "/reports/packages",
        icon: Package,
        color: "text-violet-600",
        bgColor: "bg-violet-50",
        borderColor: "border-violet-200 hover:border-violet-400",
      },
    ],
  },
  {
    title: "Halls & rooms",
    reports: [
      {
        title: "Venue Utilization",
        description: "Which halls and rooms are taken, day by day",
        href: "/reports/venues",
        icon: Building2,
        color: "text-teal-600",
        bgColor: "bg-teal-50",
        borderColor: "border-teal-200 hover:border-teal-400",
      },
      {
        title: "Room Revenue",
        description: "Room income, nights and occupancy by room and type",
        href: "/reports/rooms",
        icon: BedDouble,
        color: "text-amber-700",
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200 hover:border-amber-400",
        moneyOnly: true,
        isNew: true,
      },
    ],
  },
  {
    title: "Vendors & spending",
    reports: [
      {
        title: "Vendor Reports",
        description: "Vendor work, payments, commission and comparison",
        href: "/reports/vendors/performance",
        icon: Truck,
        color: "text-cyan-700",
        bgColor: "bg-cyan-50",
        borderColor: "border-cyan-200 hover:border-cyan-400",
      },
      {
        title: "Expenses Report",
        description: "Spending by category and period",
        href: "/reports/expenses",
        icon: Receipt,
        color: "text-amber-600",
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200 hover:border-amber-400",
      },
    ],
  },
];

export default function ReportsHubPage() {
  const canSeeMoney = useCanSeeMoney();
  const sections = SECTIONS
    .map((s) => ({ ...s, reports: s.reports.filter((r) => !r.moneyOnly || canSeeMoney) }))
    .filter((s) => s.reports.length > 0);
  const count = sections.reduce((n, s) => n + s.reports.length, 0);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            <h1 className="text-lg font-bold text-stone-800">Reports</h1>
          </div>
          <span className="text-xs text-stone-500">{count} reports</span>
        </div>

        {sections.map((section) => (
          <section key={section.title} className="space-y-2">
            <h2 className="text-xs font-bold uppercase tracking-wide text-stone-500">{section.title}</h2>
            <div className="grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {section.reports.map((report) => {
                const Icon = report.icon;
                return (
                  <Link
                    key={report.title}
                    to={report.href}
                    className={`group relative ${report.bgColor} rounded-lg p-4 border ${report.borderColor} transition-all duration-200 hover:shadow-md hover:-translate-y-0.5`}
                  >
                    {report.isNew && (
                      <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wide bg-white/90 text-stone-700 border border-stone-200 px-1.5 py-0.5 rounded-full">
                        New
                      </span>
                    )}
                    <div className={`w-8 h-8 rounded-lg bg-white/80 flex items-center justify-center mb-2 ${report.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <h3 className="text-sm font-semibold text-stone-800 mb-0.5">{report.title}</h3>
                    <p className="text-xs text-stone-600 line-clamp-2 mb-2">{report.description}</p>
                    <div className={`flex items-center gap-1 text-xs font-medium ${report.color} transition-transform group-hover:translate-x-0.5`}>
                      Open
                      <ArrowRight className="w-3 h-3" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}

        <p className="text-xs text-stone-500">
          Revenue in reports is after discount and before GST. GST is shown on its own and is never counted as income or profit.
          Cancelled events never count as revenue, profit or money owed. Reports can be downloaded to Excel or printed,
          and the date options include the financial year (April–March).
        </p>
      </div>
    </DashboardLayout>
  );
}
